import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import NestableClickable from 'components/Common/NestableClickable'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import config from 'constants/config'
import Typography from 'LuxKit/Typography/Typography'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useInView } from 'react-intersection-observer'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import ExperienceTilePrices from './ExperienceTilePrices'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import NumberRating from 'components/Common/NumberRating'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'

const ExperienceImageContainer = styled.div`
  position: relative;
  height: ${rem(162)};
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const BadgeContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(8)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(2)};
`

const Tile = styled(NestableClickable)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(364)};
  }
`

const TileContent = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;

  > * + * {
    margin-top: ${rem(8)};
  }
`

interface Props {
  experience: App.ExperienceOffer
  position?: number
}

function ExperienceCompactSearchTile(props: Props) {
  const { experience, position } = props

  const trackEvent = useTrackExperienceOfferEvent()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  useEffect(() => {
    if (inView && imageLoaded && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: experience,
        position,
        key: EventDataKey.ProductImpression,
      })

      trackEvent('OFFER_TILE_IMPRESSION', [{
        type: 'impression',
        data: {
          offer: experience,
        },
      }])
    }
  }, [inView, imageLoaded, position, dispatchOfferListEvent, trackEvent, experience])

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const tripId = useAppSelector(getImmersiveTripId)

  const offerPageQueryParams = useMemo(() => {
    if (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) return new URLSearchParams({ tripId })
    return undefined
  }, [tripId])

  const handleViewOffer = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experience,
        position,
        key: EventDataKey.ProductClick,
      })
    }

    trackEvent('OFFER_TILE_CLICKED', [{
      type: 'click',
      data: {
        offer: experience,
      },
    }])
  }, [experience, dispatchOfferListEvent, position, trackEvent])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(experience, {}),
  ], [experience])

  return (
    <Tile
      to={getExperienceOfferPageURL(experience, offerPageQueryParams)}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
      rel={config.OPEN_NEW_TAB_OFFER_CLICK ? 'noopener noreferrer' : undefined}
      onClick={handleViewOffer}
      ref={inViewRef}
    >
      <ExperienceImageContainer>
        {experience.images[0] && <Image
          height={126}
          image={experience.images[0]}
          fit="center"
          dpr={2}
          onLoad={() => setImageLoaded(true)}
        />}
        <BadgeContainer>
          <TripGuard>
            <ExperienceBookmarkButton offer={experience} withLabel="always" size="small" />
          </TripGuard>
        </BadgeContainer>
      </ExperienceImageContainer>
      <TileContent>
        <div>
          <VerticalSpacer gap={8}>
            <ExperienceAppDiscountTag experienceOffer={experience} />
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={4}>
              <VerticalSpacer gap={2}>
                <Typography wrap="pretty">
                  {experience.location && <>
                    <Caption as="span" variant="large" weight="normal">{experience.location.city ?? experience.location.name}</Caption>{' '}
                    <Caption as="span" variant="large" weight="normal">-</Caption>{' '}
                    <Caption as="span" variant="large" weight="normal">{experience.location.countryCode}</Caption>
                  </>}
                </Typography>
              </VerticalSpacer>
              {config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) && <NumberRating
                rating={experience.rating?.score}
                total={experience.rating?.reviewsTotal}
                variant="xs"
                numberPosition="start"
                onlyNumber
              />}
            </Group>
            <BodyText title={experience.name} lineClamp={3} variant="medium" weight="bold">{experience.name}</BodyText>
            {experience.cancellationPolicies.length > 0 &&
              <ExperienceCancellationText
                cancellationInfo={experience.copy.cancellationInfo}
                textSize="small"
              />}
            <LoyaltyPointsEarn
              calculationRequests={pointsEarnCalculationRequests}
              calculationType="estimate"
              hideStatusCredits
            />
          </VerticalSpacer>
        </div>
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
            <div>
              <ExperienceTilePrices
                totalPrice={experience.price}
                totalMemberPrice={experience.pricing.memberPrice}
                isFromPrice
                ticketUnitLabel={experience.ticketUnitLabel ?? 'total'}
                size="L"
                nonMemberPriceSize="L"
              />
              {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={Math.ceil(experience.grossPrice)}
                discountPercentage={experience.discount}
              />}
              <PriceRowAgentHubCommission
                offerId={experience.id}
                productType={productType}
                offerLocations={[experience.location.name]}
                offer={experience}
              />
            </div>
            <TextButton kind="primary" nonInteractable>
              View
            </TextButton>
          </Group>
        </VerticalSpacer>
      </TileContent>
    </Tile>
  )
}

export default ExperienceCompactSearchTile
